import { Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subscription } from 'rxjs';
import {
  LegalConsentService,
  PLATFORM_TERMS_VERSION_MISMATCH,
} from '../../../core/services/api/legal-consent.service';
import {
  InterruptBody,
  InterruptHostRef,
} from '../../../core/services/post-login-interrupt/post-login-interrupt.model';
import { ToastService } from '../../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * P2P-28 · F0.3 — the platform T&C re-acceptance gate body.
 *
 * Raised as a mandatory `gate` post-login interrupt when the server reports the user is not
 * current on the newest published PlatformTerms version. Blocking: the modal has no dismiss on
 * the single-interrupt path (static backdrop, no keyboard), and accepting is the only way out.
 *
 * Acceptance is pinned to the version the user was SHOWN. The version id is sent with the
 * accept request and the server rejects it if a newer version has been published since, so the
 * user is never recorded as accepting text they did not see. In that case — and when the version
 * could not be determined in the first place (the status check failed, so the gate was queued
 * fail-closed) — this body re-reads the status and shows the current version, rather than
 * offering something to accept blind.
 *
 * Reuses the existing interrupt-body pattern (cf. ForceUpdateMessageComponent) rather than
 * introducing a parallel login flow — the consolidated T&C page itself is opened in a new
 * tab for review.
 */
@Component({
  selector: 'app-platform-terms-reacceptance',
  templateUrl: './platform-terms-reacceptance.component.html',
  styleUrls: ['./platform-terms-reacceptance.component.scss'],
  standalone: false,
})
export class PlatformTermsReacceptanceComponent implements InterruptBody, OnDestroy {
  /** Version label the user is accepting, e.g. "v3.0". Null when it could not be determined. */
  @Input() version: string | null = null;

  /** Id of the version being shown. Null until known; accepting is disabled while it is. */
  @Input() versionId: number | null = null;

  /** INC-328 — present when this body is one page of the interrupt frame. */
  @Input() host?: InterruptHostRef;

  /** Accepted for the uniform host contract; this body draws no chrome of its own. */
  @Input() hostOwnsChrome?: boolean;

  agreed = false;
  submitting = false;
  loadingVersion = false;

  /** True after the terms changed under the user, so they know to review them again. */
  versionChanged = false;

  // Tied to this component's lifetime. If the session ends (logout, user switch) mid-request the
  // interrupt queue dismisses and destroys this component; an unguarded subscription would still
  // fire on a late response and call host.resolve() against a stale host, settling the NEXT
  // session's gate (same interrupt key) without their acceptance.
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly legalConsentService: LegalConsentService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
  ) {}

  get versionKnown(): boolean {
    return this.versionId !== null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  accept(): void {
    if (!this.agreed || this.submitting || this.versionId === null) {
      return;
    }
    this.submitting = true;
    this.subscriptions.add(
      this.legalConsentService
        .acceptPlatformTerms(this.versionId)
        .pipe(finalize(() => (this.submitting = false)))
        .subscribe({
          next: () => this.resolve(),
          error: (err) => {
            if (err?.error?.code === PLATFORM_TERMS_VERSION_MISMATCH) {
              this.loadVersion(true);
              return;
            }
            this.toast.error(this.translate.instant('login.platformTermsReacceptance.error'));
          },
        }),
    );
  }

  /** Re-reads which version is in force and shows it; resolves if the user turns out to be current. */
  loadVersion(announceChange = false): void {
    if (this.loadingVersion) {
      return;
    }
    this.loadingVersion = true;
    this.subscriptions.add(
      this.legalConsentService
        .getPlatformTermsReacceptanceStatus()
        .pipe(finalize(() => (this.loadingVersion = false)))
        .subscribe({
          next: (status) => {
            if (status?.needsReacceptance === false) {
              this.resolve();
              return;
            }
            this.version = status?.currentVersion ?? null;
            this.versionId = status?.currentVersionId ?? null;
            this.agreed = false;
            this.versionChanged = announceChange && this.versionId !== null;
            if (this.versionId === null) {
              this.toast.error(this.translate.instant('login.platformTermsReacceptance.versionError'));
            }
          },
          error: () => {
            // Whatever was on screen is stale or unknown; do not leave an accept button behind it.
            this.versionId = null;
            this.version = null;
            this.agreed = false;
            this.toast.error(this.translate.instant('login.platformTermsReacceptance.versionError'));
          },
        }),
    );
  }

  private resolve(): void {
    if (this.host) {
      this.host.resolve();
      return;
    }
    this.modal.close();
  }
}
